import React, { useContext } from "react";
import { SearchResultPlan } from "../../@types";
import { CheckmarkIcon } from "..";
import { isAvailable, useTranslate, PreviewContext } from "../../helpers";
import CollapsibleBox from "../CollapsibleBox";

interface PlanBenefitsProps {
  plan: SearchResultPlan;
}

const PlanBenefits: React.FunctionComponent<PlanBenefitsProps> = ({ plan }) => {
  const t = useTranslate();
  const previewMode = useContext(PreviewContext);
  const {
    package_services: services,
    transportation,
    silver_sneakers,
    worldwide_emergency,
    telehealth,
    otc_drugs,
    in_home_support,
    home_safety_devices,
    emergency_response_device,
  } = plan;

  if (!services) {
    return null;
  }

  const accessibilitySpan = (available: boolean): JSX.Element => (
    <span className="ds-u-visibility--screen-reader">
      {available
        ? ` ${t("plan_card.benefit.is_available")}`
        : ` ${t("plan_card.benefit.is_not_available")}`}
    </span>
  );

  return (
    <div className="PlanCard__section" data-testid="benefits">
      <h3 className="PlanCard__section_heading">
        {t("plan_card.section_heading.plan_benefits")}
      </h3>
      <ul className="PlanCard__benefits">
        <li>
          <CheckmarkIcon checked={isAvailable(services.vision_services)} />{" "}
          {t("plan_terms.vision")}
          {accessibilitySpan(isAvailable(services.vision_services))}
        </li>
        <li>
          <CheckmarkIcon checked={isAvailable(services.dental_services)} />{" "}
          {t("plan_terms.dental")}
          {accessibilitySpan(isAvailable(services.dental_services))}
        </li>
        <li>
          <CheckmarkIcon checked={services.ms_hearing_services} />{" "}
          {t("plan_terms.hearing")}
          {accessibilitySpan(services.ms_hearing_services)}
        </li>
        <li>
          <CheckmarkIcon checked={transportation} />{" "}
          {t("plan_terms.transportation")}
          {accessibilitySpan(transportation)}
        </li>
        <li>
          <CheckmarkIcon checked={silver_sneakers} />{" "}
          {t("plan_terms.fitness_benefits")}
          {accessibilitySpan(silver_sneakers)}
        </li>
        <li>
          <CheckmarkIcon checked={worldwide_emergency} />{" "}
          {t("plan_terms.worldwide_emergency")}
          {accessibilitySpan(worldwide_emergency)}
        </li>
        <li>
          <CheckmarkIcon checked={telehealth} /> {t("plan_terms.telehealth")}
          {accessibilitySpan(telehealth)}
        </li>
      </ul>
      <CollapsibleBox
        labelId="plan_terms.see_more_benefits"
        titleClassname="PlanCard__collapsible"
        open={previewMode}
      >
        <ul className="PlanCard__benefits">
          <li>
            <CheckmarkIcon checked={otc_drugs} /> {t("plan_terms.otc_drugs")}
            {accessibilitySpan(otc_drugs)}
          </li>
          <li>
            <CheckmarkIcon checked={in_home_support} />{" "}
            {t("plan_terms.in_home_support")}
            {accessibilitySpan(in_home_support)}
          </li>
          <li>
            <CheckmarkIcon checked={home_safety_devices} />{" "}
            {t("plan_terms.home_safety_devices")}
            {accessibilitySpan(home_safety_devices)}
          </li>
          <li>
            <CheckmarkIcon checked={emergency_response_device} />{" "}
            {t("plan_terms.emergency_response_device")}
            {accessibilitySpan(emergency_response_device)}
          </li>
        </ul>
      </CollapsibleBox>
    </div>
  );
};

export default PlanBenefits;
